import { useState, useEffect } from "react";
import { Card } from "../../primitives/card";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import { Input } from "../../primitives/input";
import { Textarea } from "../../primitives/textarea";
import { Label } from "../../primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import type {
  IssueRecord,
  IssueSaveInvestigation,
  IssueResolve,
} from "@shared-core/data/api-types";
import type { UserListItem } from "@shared-core/data/api/users";
import { ReadField } from "./IssueFields";

const ISSUE_DEPARTMENTS = [
  "Pre-press",
  "Print",
  "Finishing",
  "Despatch",
  "Customer service",
  "Estimating",
] as const;

const CAUSE_REASONS: Record<string, string[]> = {
  "Auditor Reporting": [
    "Major Non-Conformance",
    "Non-Conformance",
    "Observation",
  ],
  Courier: [
    "Damage Goods",
    "Goods Lost in Transit",
    "Part Delivery",
    "Missing Delivery",
    "Delivered to Wrong Address",
    "Delivered Wrong Items",
    "Attitude and Demeanour",
    "Hub/System Failure",
  ],
  "Customer Error": [
    "Wrong Files Supplied",
    "Incorrect Information",
    "Data Breach",
    "DPA2018/GDPR Breach",
    "Files not Supplied In Time Required",
  ],
  "Data Breach": [
    "Data Breach",
    "DPA2018/GDPR Breach",
    "Systems Breach/Hacking",
  ],
  Environmental: [
    "Substance Spill",
    "Warning Information",
    "Unsafe Site",
    "Unsafe Equipment",
    "Campus",
    "Fire Hazzard",
    "Electrical Hazzard",
    "Risk Assessment",
    "COSHH",
    "Slips/Trip/Fall",
    "Power Failure",
    "Unsafe Vehicle",
  ],
  "Health & Safety": [
    "Substance Spill",
    "Warning Information",
    "Unsafe Site",
    "Unsafe Equipment",
    "Campus",
    "Fire Hazzard",
    "Electrical Hazzard",
    "Risk Assessment",
    "COSHH",
    "Slips/Trip/Fall",
    "Power Failure",
    "Unsafe Vehicle",
    "Near Miss",
    "Dangerous Occurance",
  ],
  "Human Factor": [
    "Job Bag Not Read",
    "Job Bag Incorrect",
    "Job Bag Not Understood",
    "Missing Information",
    "Training Not Given",
    "Training Not Understood",
    "SOP Not Followed",
    "SOP Unclear",
    "SOP Procedure Not Covered",
    "Slips/Trip/Fall",
    "Policy Not Followed",
    "Data Entry",
    "Secondary Checks Missed Error",
    "Operator Error",
    "Wrong Item Picked",
  ],
  "IT Issue": [
    "Machine Breakdown",
    "Machine Not Maintained",
    "Systems Failure",
    "Hardware Failure",
    "Software Failure",
    "Calibration Error",
    "Systems Breach/Hacking",
    "Code Error",
    "Bug Fixes",
  ],
  "Machine Error": [
    "Machine Breakdown",
    "Machine Not Maintained",
    "Systems Failure",
    "Hardware Failure",
    "Software Failure",
    "Calibration Error",
  ],
  "Manufacturing Issue": [
    "Wrong Goods Supplied",
    "Damaged Goods Received",
    "Wrong Quantity Supplied",
    "Delivery Date Not Met",
    "Quality Issue",
    "Comunication",
    "Colour Management Issue",
  ],
  "Paper Issue": [
    "Wrong Paper Issued",
    "Damaged Stock",
    "Wrong Paper Delivered",
    "Wrong Paper Supplied by Client",
  ],
  "Service Level": [
    "SLA Not Met",
    "Poor Service",
    "Delay in Response Time",
    "No Response",
    "Delivery Date Not Met",
    "Comunication",
  ],
  "Supplier Error": [
    "Wrong Goods Supplied",
    "Damaged Goods Received",
    "Wrong Quantity Supplied",
    "Delivery Date Not Met",
    "Quality Issue",
    "Poor Service",
    "Delay in Response Time",
    "No Response",
    "Comunication",
  ],
};

const CAUSES = Object.keys(CAUSE_REASONS);

interface InvForm {
  investigation_findings: string;
  investigation_identified_cause: string;
  investigation_root_reason: string;
  investigation_attribution_department: string | null;
  investigation_attribution_person_id: string | null;
  investigation_cost_direct: number | null;
  investigation_cost_indirect: number | null;
  // Resolution section
  investigation_corrective_action: string;
  resolution_assignee_id: string | null;
  resolution_due_date: string;
  resolution_actions_completed: boolean;
}

function issueToForm(issue: IssueRecord): InvForm {
  return {
    investigation_findings: issue.investigation_findings ?? "",
    investigation_identified_cause: issue.investigation_identified_cause ?? "",
    investigation_root_reason: issue.investigation_root_reason ?? "",
    investigation_attribution_department:
      issue.investigation_attribution_department ?? null,
    investigation_attribution_person_id:
      issue.investigation_attribution_person_id ?? null,
    investigation_cost_direct:
      issue.investigation_cost_direct != null
        ? Number(issue.investigation_cost_direct)
        : null,
    investigation_cost_indirect:
      issue.investigation_cost_indirect != null
        ? Number(issue.investigation_cost_indirect)
        : null,
    investigation_corrective_action:
      issue.investigation_corrective_action ?? "",
    resolution_assignee_id: issue.resolution_assignee_id ?? null,
    resolution_due_date: issue.resolution_due_date ?? "",
    resolution_actions_completed: issue.resolution_actions_completed ?? false,
  };
}

function hasAnyInvestigation(issue: IssueRecord): boolean {
  return !!(
    issue.investigation_findings ||
    issue.investigation_identified_cause ||
    issue.investigation_root_reason ||
    issue.investigation_corrective_action
  );
}

interface IssueInvestigationTabProps {
  issue: IssueRecord;
  editable: boolean;
  saving: boolean;
  resolving: boolean;
  resolverUsers: UserListItem[];
  onSave: (data: IssueSaveInvestigation) => Promise<void>;
  onResolve: (data: IssueResolve) => void;
}

function numberOrNull(raw: string): number | null {
  if (raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function IssueInvestigationTab({
  issue,
  editable,
  saving,
  resolving,
  resolverUsers,
  onSave,
  onResolve,
}: IssueInvestigationTabProps) {
  const [form, setForm] = useState<InvForm>(() => issueToForm(issue));
  const [jobNoteBody, setJobNoteBody] = useState("");

  useEffect(() => {
    setForm(issueToForm(issue));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form deliberately resets only on the listed issue fields, not on every issue object identity change
  }, [
    issue.id,
    issue.investigation_findings,
    issue.investigation_corrective_action,
    issue.resolution_actions_completed,
  ]);

  // Read-only view (legacy records, or any non-editing context).
  if (!editable) {
    if (!hasAnyInvestigation(issue)) {
      return (
        <Card className="p-6">
          <p className="text-sm italic text-muted-foreground">
            Investigation has not started yet.
          </p>
        </Card>
      );
    }
    return (
      <Card className="p-6 space-y-5">
        <ReadField label="Findings" value={issue.investigation_findings} />
        <div className="grid sm:grid-cols-2 gap-5">
          <ReadField
            label="Identified cause"
            value={issue.investigation_identified_cause}
          />
          <ReadField
            label="Root reason"
            value={issue.investigation_root_reason}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <ReadField
            label="Attributed to"
            value={[
              issue.investigation_attribution_department,
              issue.investigation_attribution_person_id,
            ]
              .filter(Boolean)
              .join(" · ")}
            empty="Not attributed"
          />
          <ReadField
            label="Cost"
            value={
              issue.investigation_cost_direct == null &&
              issue.investigation_cost_indirect == null
                ? null
                : `${formatCurrencyGbp(Number(issue.investigation_cost_direct ?? 0))} direct · ${formatCurrencyGbp(
                    Number(issue.investigation_cost_indirect ?? 0),
                  )} indirect`
            }
            empty="Not costed"
          />
        </div>
        <div className="border-t pt-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4">
            Resolution
          </p>
          <div className="space-y-4">
            <ReadField
              label="Corrective action"
              value={issue.investigation_corrective_action}
            />
            <div className="grid sm:grid-cols-2 gap-5">
              <ReadField
                label="Assigned to"
                value={
                  issue.resolution_assignee_name ?? issue.resolution_assignee_id
                }
                empty="Not assigned"
              />
              <ReadField
                label="Complete by"
                value={issue.resolution_due_date ?? null}
                empty="No due date"
              />
            </div>
            <ReadField
              label="Actions completed"
              value={issue.resolution_actions_completed ? "Yes" : "No"}
            />
          </div>
        </div>
      </Card>
    );
  }

  const isResolved = issue.status === "resolved";
  const canResolve =
    !!form.investigation_corrective_action.trim() &&
    form.resolution_actions_completed;

  function buildSaveData(): IssueSaveInvestigation {
    return {
      investigation_findings: form.investigation_findings || null,
      investigation_identified_cause:
        form.investigation_identified_cause || null,
      investigation_root_reason: form.investigation_root_reason || null,
      investigation_attribution_department:
        form.investigation_attribution_department,
      investigation_attribution_person_id:
        form.investigation_attribution_person_id,
      investigation_cost_direct: form.investigation_cost_direct,
      investigation_cost_indirect: form.investigation_cost_indirect,
      investigation_corrective_action:
        form.investigation_corrective_action || null,
      resolution_assignee_id: form.resolution_assignee_id,
      resolution_due_date: form.resolution_due_date || null,
      resolution_actions_completed: form.resolution_actions_completed,
      investigation_job_note_body: jobNoteBody.trim() || null,
    };
  }

  async function handleSave() {
    await onSave(buildSaveData());
    setJobNoteBody("");
  }

  async function handleResolve() {
    await onSave(buildSaveData());
    setJobNoteBody("");
    onResolve({});
  }

  return (
    <Card className="p-6 space-y-5">
      <div>
        <Label htmlFor="inv-findings">Investigation findings</Label>
        <Textarea
          id="inv-findings"
          className="mt-1.5 min-h-24"
          value={form.investigation_findings}
          onChange={(e) =>
            setForm({ ...form, investigation_findings: e.target.value })
          }
          placeholder="What the investigation established."
          disabled={isResolved}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Identified cause</Label>
          <Select
            value={form.investigation_identified_cause}
            onValueChange={(cause) => {
              const validReasons = CAUSE_REASONS[cause] ?? [];
              const currentReason = form.investigation_root_reason;
              setForm({
                ...form,
                investigation_identified_cause: cause,
                investigation_root_reason: validReasons.includes(currentReason)
                  ? currentReason
                  : "",
              });
            }}
            disabled={isResolved}
          >
            <SelectTrigger className="mt-1.5 w-full">
              <SelectValue placeholder="Select cause…" />
            </SelectTrigger>
            <SelectContent>
              {CAUSES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Root reason</Label>
          <Select
            value={form.investigation_root_reason}
            onValueChange={(reason) =>
              setForm({ ...form, investigation_root_reason: reason })
            }
            disabled={isResolved || !form.investigation_identified_cause}
          >
            <SelectTrigger className="mt-1.5 w-full">
              <SelectValue
                placeholder={
                  form.investigation_identified_cause
                    ? "Select reason…"
                    : "Select a cause first"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {(CAUSE_REASONS[form.investigation_identified_cause] ?? []).map(
                (r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Department attributable</Label>
          <Select
            value={form.investigation_attribution_department ?? "none"}
            onValueChange={(v) =>
              setForm({
                ...form,
                investigation_attribution_department: v === "none" ? null : v,
              })
            }
            disabled={isResolved}
          >
            <SelectTrigger className="mt-1.5 w-full">
              <SelectValue placeholder="Select department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Not attributed</SelectItem>
              {ISSUE_DEPARTMENTS.map((d) => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="inv-person">Person attributable (optional)</Label>
          <Input
            id="inv-person"
            className="mt-1.5"
            value={form.investigation_attribution_person_id ?? ""}
            onChange={(e) =>
              setForm({
                ...form,
                investigation_attribution_person_id: e.target.value || null,
              })
            }
            placeholder="Leave blank unless directly attributable"
            disabled={isResolved}
          />
        </div>
      </div>

      <div>
        <Label>Cost</Label>
        <div className="mt-1.5 grid sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Direct £</span>
            <Input
              type="number"
              inputMode="decimal"
              value={form.investigation_cost_direct ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  investigation_cost_direct: numberOrNull(e.target.value),
                })
              }
              placeholder="0"
              disabled={isResolved}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Indirect £</span>
            <Input
              type="number"
              inputMode="decimal"
              value={form.investigation_cost_indirect ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  investigation_cost_indirect: numberOrNull(e.target.value),
                })
              }
              placeholder="0"
              disabled={isResolved}
            />
          </div>
        </div>
      </div>

      {/* ── Resolution section ── */}
      <div className="border-t pt-5 space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Resolution
        </p>

        <div>
          <Label htmlFor="inv-corrective">Corrective action</Label>
          <Textarea
            id="inv-corrective"
            className="mt-1.5 min-h-24"
            value={form.investigation_corrective_action}
            onChange={(e) =>
              setForm({
                ...form,
                investigation_corrective_action: e.target.value,
              })
            }
            placeholder="What was done, or will be done, to stop this recurring."
            disabled={isResolved}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Assign to resolver</Label>
            <Select
              value={form.resolution_assignee_id ?? "none"}
              onValueChange={(v) =>
                setForm({
                  ...form,
                  resolution_assignee_id: v === "none" ? null : v,
                })
              }
              disabled={isResolved}
            >
              <SelectTrigger className="mt-1.5 w-full">
                <SelectValue placeholder="Select person…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not assigned</SelectItem>
                {resolverUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="inv-due-date">Complete by</Label>
            <Input
              id="inv-due-date"
              type="date"
              className="mt-1.5"
              value={form.resolution_due_date}
              onChange={(e) =>
                setForm({ ...form, resolution_due_date: e.target.value })
              }
              disabled={isResolved}
            />
          </div>
        </div>

        <div className="flex items-start gap-3 pt-1">
          <Checkbox
            id="actions-completed"
            checked={form.resolution_actions_completed}
            onCheckedChange={(checked) =>
              setForm({
                ...form,
                resolution_actions_completed: checked === true,
              })
            }
            disabled={isResolved}
          />
          <div>
            <Label htmlFor="actions-completed" className="cursor-pointer">
              Actions completed
            </Label>
            <p className="text-sm text-muted-foreground mt-0.5">
              Tick when the corrective action has been carried out. This allows
              the investigator to formally resolve the issue.
            </p>
          </div>
        </div>
      </div>

      {issue.job_no && !isResolved && (
        <div className="border-t pt-5 space-y-2">
          <Label htmlFor="inv-job-note">Tharstern job note (optional)</Label>
          <Textarea
            id="inv-job-note"
            className="mt-1.5"
            value={jobNoteBody}
            onChange={(e) => setJobNoteBody(e.target.value)}
            placeholder="Leave a note on the Tharstern job — it will pop up when the job is opened or copied."
          />
          <p className="text-xs text-muted-foreground">
            Tharstern job {issue.job_no} ·{" "}
            {issue.tharstern_note_id
              ? "Updates the existing note"
              : "Creates a new priority note"}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 border-t pt-4">
        <Button
          variant="outline"
          onClick={handleSave}
          disabled={saving || isResolved}
        >
          {saving ? "Saving…" : "Save investigation"}
        </Button>
        {!isResolved && (
          <div className="flex items-center gap-3">
            {!canResolve && (
              <p className="text-sm text-muted-foreground">
                {!form.investigation_corrective_action.trim()
                  ? "Add a corrective action to resolve"
                  : "Mark actions completed to resolve"}
              </p>
            )}
            <Button onClick={handleResolve} disabled={resolving || !canResolve}>
              {resolving ? "Resolving…" : "Resolve issue"}
            </Button>
          </div>
        )}
        {isResolved && (
          <span className="text-sm text-[color:var(--color-success)] font-medium">
            Resolved
          </span>
        )}
      </div>
    </Card>
  );
}
